/**
 * Upgrade Page
 *
 * Explains Cloud Studio benefits and handles upgrade flow.
 * Shows migration progress when transitioning from local to cloud.
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Sparkles,
  Cloud,
  HardDrive,
  Check,
  X,
  Loader2,
  ArrowRight,
  ChevronLeft,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Button, Card, CardContent } from '@/components/ui'
import { useStudio } from '@/context/StudioContext'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'

interface FeatureRow {
  feature: string
  local: boolean | string
  cloud: boolean | string
}

const FEATURES: FeatureRow[] = [
  { feature: 'Stories per child', local: '1', cloud: 'Unlimited' },
  { feature: 'Where stories are saved', local: 'This device only', cloud: 'Cloud (any device)' },
  { feature: 'Missions & curriculum', local: true, cloud: true },
  { feature: 'Progress dashboard', local: false, cloud: true },
  { feature: 'Badges & achievements', local: false, cloud: true },
  { feature: 'Skill tracking', local: false, cloud: true },
  { feature: 'Export stories', local: false, cloud: true },
  { feature: 'Multi-device access', local: false, cloud: true },
]

export function Upgrade() {
  const { mode, hasPendingMigration, isMigrating, triggerMigration } = useStudio()
  const { family, refreshFamily } = useFamily()
  const { showToast, celebrate } = useToast()

  const [isUpgrading, setIsUpgrading] = useState(false)
  const [migrationTriggered, setMigrationTriggered] = useState(false)

  // If already in cloud mode, show success state
  const isCloudMode = mode === 'cloud'

  // Handle migration when mode changes to cloud
  useEffect(() => {
    if (isCloudMode && hasPendingMigration && !migrationTriggered && !isMigrating) {
      setMigrationTriggered(true)
      triggerMigration().then((success) => {
        if (success) {
          celebrate('Your story has been synced to the cloud!')
        }
      })
    }
  }, [isCloudMode, hasPendingMigration, migrationTriggered, isMigrating, triggerMigration, celebrate])

  // Handle upgrade button click - payments coming soon
  const handleUpgrade = () => {
    showToast('Payments coming soon! We\'ll let you know when Cloud Studio is available.', 'info')
  }

  // Dev-only: Downgrade back to free
  const handleDevDowngrade = async () => {
    if (!family || !isSupabaseConfigured()) return

    setIsUpgrading(true)
    try {
      const { error } = await supabase
        .from('families')
        .update({
          subscription_tier: 'free',
          subscription_status: null,
          subscription_started_at: null,
          subscription_ends_at: null,
        })
        .eq('id', family.id)

      if (error) {
        showToast(`Downgrade failed: ${error.message}`, 'error')
        return
      }

      await refreshFamily()
      showToast('Downgraded to Local Studio', 'success')
    } catch (err) {
      console.error('Downgrade error:', err)
    } finally {
      setIsUpgrading(false)
    }
  }

  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === 'string') {
      return <span className="font-medium">{value}</span>
    }
    return value ? (
      <Check className="w-5 h-5 text-[var(--color-kids-success)]" />
    ) : (
      <X className="w-5 h-5 text-[var(--color-kids-text-muted)] opacity-40" />
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-[var(--color-kids-primary)]" />
            <span className="font-display text-xl font-bold text-[var(--color-kids-text)]">
              Inkwell Jr.
            </span>
          </Link>

          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto p-6">
        {/* Success state when already upgraded */}
        {isCloudMode ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="max-w-lg mx-auto">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-[var(--color-kids-success)]20 rounded-full flex items-center justify-center">
                  <Cloud className="w-10 h-10 text-[var(--color-kids-success)]" />
                </div>

                <h1 className="font-display text-2xl font-bold text-[var(--color-kids-text)] mb-2">
                  Cloud Studio is Active
                </h1>

                {isMigrating ? (
                  <div className="mt-6">
                    <div className="flex items-center justify-center gap-3 text-[var(--color-kids-primary)]">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Syncing your local story to the cloud...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-[var(--color-kids-text-muted)] mb-6">
                      Your stories are saved to the cloud and accessible from any device.
                    </p>

                    <div className="flex flex-col gap-3">
                      <Link to="/stories">
                        <Button size="lg" className="w-full">
                          Go to My Stories
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </Link>

                      {/* Dev-only downgrade button */}
                      {import.meta.env.DEV && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDevDowngrade}
                          disabled={isUpgrading}
                          className="text-red-500 hover:text-red-600"
                        >
                          {isUpgrading ? 'Processing...' : '[DEV] Downgrade to Free'}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <>
            {/* Page header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <h1 className="font-display text-[var(--font-size-kid-2xl)] font-bold text-[var(--color-kids-text)] mb-3">
                Upgrade to Cloud Studio
              </h1>
              <p className="text-lg text-[var(--color-kids-text-muted)] max-w-xl mx-auto">
                Save stories safely, unlock progress tracking, and write on any device.
              </p>
            </motion.div>

            {/* Comparison table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="mb-8 overflow-hidden">
                <CardContent className="p-0">
                  {/* Table header */}
                  <div className="grid grid-cols-3 bg-[var(--color-kids-surface-alt)]">
                    <div className="p-4 font-semibold text-[var(--color-kids-text)]">
                      Feature
                    </div>
                    <div className="p-4 text-center border-l border-[var(--color-kids-border)]">
                      <div className="flex items-center justify-center gap-2">
                        <HardDrive className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
                        <span className="font-semibold text-[var(--color-kids-text)]">
                          Local Studio
                        </span>
                      </div>
                      <span className="text-sm text-[var(--color-kids-text-muted)]">Free</span>
                    </div>
                    <div className="p-4 text-center border-l border-[var(--color-kids-border)] bg-[var(--color-kids-primary)]10">
                      <div className="flex items-center justify-center gap-2">
                        <Cloud className="w-5 h-5 text-[var(--color-kids-primary)]" />
                        <span className="font-semibold text-[var(--color-kids-text)]">
                          Cloud Studio
                        </span>
                      </div>
                      <span className="text-sm text-[var(--color-kids-primary)]">Premium</span>
                    </div>
                  </div>

                  {/* Feature rows */}
                  {FEATURES.map((row, index) => (
                    <div
                      key={row.feature}
                      className={`grid grid-cols-3 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-[var(--color-kids-surface)]'
                      }`}
                    >
                      <div className="p-4 text-[var(--color-kids-text)]">{row.feature}</div>
                      <div className="p-4 flex items-center justify-center border-l border-[var(--color-kids-border)] text-[var(--color-kids-text-muted)]">
                        {renderFeatureValue(row.local)}
                      </div>
                      <div className="p-4 flex items-center justify-center border-l border-[var(--color-kids-border)] bg-[var(--color-kids-primary)]05">
                        {renderFeatureValue(row.cloud)}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* Pricing & CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="max-w-md mx-auto bg-gradient-to-br from-[var(--color-kids-primary)] to-[var(--color-kids-primary-dark)] text-white">
                <CardContent className="p-8 text-center">
                  <p className="text-white/80 uppercase tracking-wide text-sm mb-2">
                    Cloud Studio
                  </p>
                  <div className="mb-4">
                    <span className="font-display text-4xl font-bold">$4.99</span>
                    <span className="text-white/80">/month</span>
                  </div>
                  <p className="text-white/80 text-sm mb-6">
                    Cancel anytime. Your stories stay safe.
                  </p>

                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full mb-4"
                    onClick={handleUpgrade}
                  >
                    Start Cloud Studio
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <p className="text-white/60 text-xs">
                    Payments launching soon
                  </p>

                  <Link
                    to="/dashboard"
                    className="text-white/80 hover:text-white text-sm underline"
                  >
                    Not now
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Migration note */}
            {hasPendingMigration && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center text-sm text-[var(--color-kids-text-muted)] mt-6"
              >
                Your local story will be automatically synced to the cloud when you upgrade.
              </motion.p>
            )}
          </>
        )}
      </main>
    </div>
  )
}
